if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const referralValue = typeof window.getReferralValue === 'function' ? window.getReferralValue() : '';
        if (referralValue) {
          formData.append('attributes[referral]', referralValue);
        }

        const addOnSelector = this.dataset.sectionId
          ? `#MainProduct-${this.dataset.sectionId} input.product-add-on:checked`
          : 'input.product-add-on:checked';
        const addOnVariantIds = Array.from(document.querySelectorAll(addOnSelector))
          .map((input) => input.value.trim())
          .filter(Boolean);

        config.body = formData;

        if (formData.get('bundle')) {
          // Remove the id and quantity for the default variant, added by the bundle JSON
          formData.delete('id');
          formData.delete('quantity');
          // Turn the bundle JSON into FormData
          config.body = this.bundleJsonToFormData(formData, formData.get('bundle'));
        }

        const run = async () => {
          // Bundles (club configurator, clubsets, headcovers) are exempt from the purchase limit.
          if (!formData.get('bundle')) {
            const requests = [
              { variantId: formData.get('id'), quantity: formData.get('quantity') || 1 },
              ...addOnVariantIds.map((variantId) => ({ variantId, quantity: 1 })),
            ];
            if (await window.PurchaseLimit.wouldExceed(requests)) {
              this.handleErrorMessage(window.cartStrings.maxPerVariantError);
              return;
            }
          }

          if (addOnVariantIds.length > 0) {
            try {
              const addOnRes = await fetch(routes.cart_add_url, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                  items: addOnVariantIds.map((id) => ({ id: Number(id), quantity: 1 })),
                }),
              }).then((res) => res.json());
              if (addOnRes.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: addOnVariantIds[0],
                  errors: addOnRes.errors || addOnRes.description,
                  message: addOnRes.message,
                });
                this.handleErrorMessage(addOnRes.description);
              }
            } catch (e) {
              console.error('[product-form] add-ons fetch error:', e);
            }
          }

          const response = await fetch(`${routes.cart_add_url}`, config).then((res) => res.json());
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButtonText.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          }
          this.captureAddToCart(response, formData, addOnVariantIds.length);

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error)
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          this.error = false;
          this.cart.renderContents(response);
        };

        run()
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      captureAddToCart(response, formData, addOnCount) {
        if (typeof window.posthog?.capture !== 'function') return;

        const item = Array.isArray(response.items) ? response.items[0] : response;
        if (!item || (!item.product_id && !item.variant_id && !item.handle)) return;

        const centsToDollars = (cents) => (typeof cents === 'number' ? cents / 100 : undefined);
        const props = {
          product_id: item.product_id != null ? String(item.product_id) : this.dataset.productId,
          product_name: item.product_title || item.title || this.dataset.productName,
          product_handle: item.handle || this.dataset.productHandle,
          product_type: item.product_type || this.dataset.productType,
          product_category: this.dataset.productCategory,
          brand: item.vendor,
          sku: item.sku,
          variant_id: item.variant_id != null ? String(item.variant_id) : formData.get('id'),
          variant: item.variant_title,
          price: centsToDollars(item.final_price ?? item.discounted_price ?? item.price),
          price_compare_at: this.dataset.priceCompareAt ? Number(this.dataset.priceCompareAt) : undefined,
          quantity: Number(item.quantity || formData.get('quantity') || 1),
          currency: window.Shopify?.currency?.active,
          url: item.url,
          image_url: item.featured_image?.url || item.image,
          has_add_on: addOnCount > 0,
        };

        Object.keys(props).forEach((key) => {
          if (props[key] === undefined || props[key] === null || props[key] === '') {
            delete props[key];
          }
        });

        window.posthog.capture('product_add_to_cart', props, this.cart ? undefined : { transport: 'sendBeacon' });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      // Convert bundle JSON to Shopify format FormData
      // Examples: items[0]id, items[0]quantity, items[0]properties[name]
      bundleJsonToFormData(formData, jsonString) {
        const { items } = JSON.parse(jsonString);

        Object.keys(items).forEach((key, index) => {
          if (typeof items[key] === 'object' && !Array.isArray(items[key])) {
            // Handle multiple nested objects
            Object.keys(items[key]).forEach((nestedKey) => {
              if (typeof items[key][nestedKey] === 'object' && !Array.isArray(items[key][nestedKey])) {
                Object.keys(items[key][nestedKey]).forEach((nestedNestedKey) => {
                  formData.append(
                    `items[${index}]${nestedKey}[${nestedNestedKey}]`,
                    items[key][nestedKey][nestedNestedKey],
                  );
                });
              } else {
                formData.append(`items[${index}]${nestedKey}`, items[key][nestedKey]);
              }
            });
          }
        });
        return formData;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
